import { useState, useEffect, useCallback } from 'react';
import { GlassPanel } from '@/components/GlassPanel';
import { Search, Filter, Users, Star, MapPin, Shield, Cpu, Zap } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REGIONS = ['all', 'Ljubljana', 'Maribor', 'Celje', 'Koper', 'Kranj', 'Nova Gorica', 'Novo mesto'];
const ROLES = ['all', 'Informator', 'Senior Informator', 'Team Lead'];

const CandidateCard = ({ candidate }) => {
  const getScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-400';
    if (score >= 80) return 'text-[#C87533]';
    return 'text-white/60';
  };

  return (
    <div
      className="p-4 sm:p-5 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-[14px] card-hover transition-all duration-200"
      data-testid="candidate-card"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-display text-sm font-semibold text-white">{candidate.name}</h4>
          <div className="flex items-center gap-1.5 mt-1">
            <MapPin className="w-3 h-3 text-white/40" />
            <span className="text-xs text-white/50">{candidate.region}</span>
            <span className="text-white/20">|</span>
            <span className="text-xs text-[#C87533]">{candidate.role}</span>
          </div>
        </div>
        <div className="text-right">
          <div className={`kpi-number text-lg font-bold ${getScoreColor(candidate.matching_score)}`}>
            {candidate.matching_score}
          </div>
          <p className="font-mono text-[10px] text-white/30">MATCH</p>
        </div>
      </div>

      {/* Score bars */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3">
        {[
          { icon: Star, label: 'EQ', value: candidate.eq_score, color: '#C87533' },
          { icon: Cpu, label: 'Tech', value: candidate.tech_affinity, color: '#4CC9F0' },
          { icon: Zap, label: 'Agility', value: candidate.agility, color: '#F5A623' },
          { icon: Shield, label: 'Brand Fit', value: candidate.brand_fit, color: '#2DD4BF' },
        ].map((metric) => (
          <div key={metric.label} className="flex items-center gap-2">
            <metric.icon className="w-3 h-3 flex-shrink-0" style={{ color: metric.color }} />
            <div className="flex-1">
              <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${metric.value}%`, backgroundColor: metric.color }}
                />
              </div>
            </div>
            <span className="font-mono text-[10px] text-white/40 w-6 text-right">{metric.value}</span>
          </div>
        ))}
      </div>

      {/* Experience and availability */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
        <span className="font-mono text-[10px] text-white/40">
          {candidate.experience_years} let izkušenj
        </span>
        <span className={`inline-flex items-center gap-1 text-[10px] font-mono ${
          candidate.available ? 'text-emerald-400' : 'text-white/30'
        }`}>
          <span className="w-1.5 h-1.5 rounded-full bg-current" />
          {candidate.available ? 'Razpoložljiv' : 'Zaseden'}
        </span>
      </div>
    </div>
  );
};

export const LiveSourcing = () => {
  const [candidates, setCandidates] = useState([]);
  const [stats, setStats] = useState(null);
  const [region, setRegion] = useState('all');
  const [role, setRole] = useState('all');
  const [loading, setLoading] = useState(false);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (region !== 'all') params.append('region', region);
      if (role !== 'all') params.append('role', role);
      params.append('available_only', 'true');
      const response = await axios.get(`${API}/candidates?${params.toString()}`);
      setCandidates(response.data);
    } catch (err) {
      console.error('Failed to fetch candidates:', err);
    } finally {
      setLoading(false);
    }
  }, [region, role]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/candidates/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <section
      id="live-sourcing"
      data-testid="live-sourcing-section"
      className="scroll-section py-[clamp(64px,8vw,120px)] px-4"
    >
      <div className="section-content max-w-[1200px] mx-auto">
        {/* Section label */}
        <div className="flex items-center gap-3 mb-8">
          <span className="w-8 h-px bg-[#C87533]" />
          <span className="font-mono text-xs text-[#C87533] uppercase tracking-wider">Live Sourcing Preview</span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-2 text-white">
              Naša baza talentov
            </h2>
            <p className="text-sm text-white/65 leading-relaxed max-w-xl">
              Prikaz števila aktivnih kandidatov, ki v tem trenutku ustrezajo kriterijem
              &quot;Informers 2026&quot;.
            </p>
          </div>
          {stats && (
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="kpi-number text-2xl font-bold text-[#C87533]">{stats.available}</p>
                <p className="font-mono text-[10px] text-white/40">AKTIVNIH</p>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div className="text-right">
                <p className="kpi-number text-2xl font-bold text-white">{stats.total}</p>
                <p className="font-mono text-[10px] text-white/40">SKUPAJ</p>
              </div>
            </div>
          )}
        </div>

        {/* Filters */}
        <GlassPanel className="p-4 mb-6" data-testid="sourcing-filter-panel">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="w-4 h-4 text-[#C87533]" />
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#C87533]/40 appearance-none cursor-pointer"
              data-testid="sourcing-filter-region-select"
            >
              <option value="all" className="bg-[#0a1628]">Vse regije</option>
              {REGIONS.filter(r => r !== 'all').map((r) => (
                <option key={r} value={r} className="bg-[#0a1628]">{r}</option>
              ))}
            </select>

            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#C87533]/40 appearance-none cursor-pointer"
              data-testid="sourcing-filter-role-select"
            >
              <option value="all" className="bg-[#0a1628]">Vse vloge</option>
              {ROLES.filter(r => r !== 'all').map((r) => (
                <option key={r} value={r} className="bg-[#0a1628]">{r}</option>
              ))}
            </select>

            <span className="ml-auto font-mono text-xs text-white/40">
              {candidates.length} rezultatov
            </span>
          </div>
        </GlassPanel>

        {/* Candidates grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-48 rounded-2xl bg-white/[0.03] animate-pulse" />
            ))}
          </div>
        ) : candidates.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {candidates.map((candidate) => (
              <CandidateCard key={candidate.id} candidate={candidate} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Users className="w-12 h-12 text-white/10 mx-auto mb-4" />
            <p className="text-white/30 text-sm">Ni kandidatov, ki ustrezajo izbranim kriterijem</p>
          </div>
        )}
      </div>
    </section>
  );
};
